import React, { useMemo } from "react";
import { ThemeProvider as MuiThemeProvider, createTheme } from "@mui/material/styles";
import { useTheme as useAppTheme } from "../contexts/ThemeContext";

/**
 * Aligns MUI components (OEE module, dialogs) with MaX charcoal/neutral branding.
 * Must render inside app ThemeProvider (data-theme on document).
 */
export default function MuiBrandThemeProvider({ children }) {
  const { theme: appTheme } = useAppTheme();
  const isDark = appTheme === "dark";

  const muiTheme = useMemo(
    () =>
      createTheme({
        palette: {
          mode: isDark ? "dark" : "light",
          primary: {
            main: isDark ? "#e4e4e7" : "#18181b",
            dark: "#0a0a0a",
            light: "#52525b",
            contrastText: isDark ? "#0a0a0a" : "#fafafa",
          },
          secondary: {
            main: "#52525b",
          },
          background: {
            default: isDark ? "#121212" : "#fafafa",
            paper: isDark ? "#1a1a1a" : "#ffffff",
          },
        },
        shape: { borderRadius: 8 },
        components: {
          MuiDialog: {
            styleOverrides: {
              paper: {
                borderRadius: '5px',
                boxShadow: isDark 
                  ? '0 25px 50px -12px rgba(0, 0, 0, 0.5)' 
                  : '0 20px 40px -8px rgba(0, 0, 0, 0.1)',
                border: isDark ? '1px solid rgba(255, 255, 255, 0.1)' : '1px solid rgba(0, 0, 0, 0.05)',
              },
            },
          },
          MuiDialogTitle: {
            styleOverrides: {
              root: {
                padding: '20px 24px',
                fontSize: '1.25rem',
                fontWeight: 700,
                color: isDark ? '#ffffff' : '#18181b', // brand text-primary
              },
            },
          },
          MuiDialogContent: {
            defaultProps: {
              dividers: true,
            },
            styleOverrides: {
              root: {
                padding: '24px',
                '&.MuiDialogContent-dividers': {
                  borderColor: isDark ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.08)',
                  paddingTop: '24px',
                  paddingBottom: '24px',
                },
              },
            },
          },
          MuiDialogActions: {
            styleOverrides: {
              root: {
                padding: '16px 24px',
                gap: '12px',
                '& > :not(:first-of-type)': {
                  marginLeft: 0,
                },
              },
            },
          },
        },
      }),
    [isDark]
  );

  return <MuiThemeProvider theme={muiTheme}>{children}</MuiThemeProvider>;
}
